import { useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "../lib/api-client";
import type { PaginatedActivityLogResponse } from "../types/activity";

interface ApiResponse {
  success: boolean;
  data: PaginatedActivityLogResponse;
}

const PAGE_LIMIT = 20;

/**
 * Activity trail for one candidate_status (project-scoped) or, with
 * `candidateId`, for a candidate across every project.
 */
export type ActivityLogScope =
  | { candidateStatusId: string | undefined; candidateId?: undefined }
  | { candidateId: string | undefined; candidateStatusId?: undefined };

export function useActivityLogs(scope: ActivityLogScope) {
  const path = scope.candidateId
    ? `/candidates/${scope.candidateId}/activity-logs`
    : `/candidate-statuses/${scope.candidateStatusId}/activity-logs`;
  const enabled = !!(scope.candidateId ?? scope.candidateStatusId);

  const query = useInfiniteQuery({
    queryKey: ["activity-logs", scope.candidateId ?? null, scope.candidateStatusId ?? null] as const,
    queryFn: ({ signal, pageParam }) =>
      apiClient.get<ApiResponse>(`${path}?page=${pageParam}&limit=${PAGE_LIMIT}`, { signal }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { total, page, limit } = lastPage.data;
      return page * limit < total ? page + 1 : undefined;
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const items = query.data?.pages.flatMap((page) => page.data.items) ?? [];

  return {
    items,
    total: query.data?.pages[0]?.data.total ?? 0,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error ? "Failed to load activity logs." : null,
    refetch: query.refetch,
  };
}
